import React from 'react';

import { Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import dayjs, { Dayjs } from 'dayjs';

/** 期間プリセット */
export const ExpensePeriodPreset = {
  THIS_MONTH: '今月',
  LAST_MONTH: '先月',
  THIS_YEAR: '今年',
  ALL: '全期間',
  CUSTOM: 'カスタム',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ExpensePeriodPreset = keyof typeof ExpensePeriodPreset;

/** 集計期間（2画面共通で user_profile に保存する値の形） */
export type ExpenseDateRange = {
  preset: ExpensePeriodPreset;
  /** preset='CUSTOM' のときのみ使用（yyyy-MM-dd） */
  from: string;
  to: string;
};

export const DEFAULT_DATE_RANGE: ExpenseDateRange = {
  preset: 'THIS_MONTH',
  from: '',
  to: '',
};

/** プリセット一覧の妥当なキー（復元ガードで使用） */
export const PERIOD_PRESET_KEYS = Object.keys(ExpensePeriodPreset) as ExpensePeriodPreset[];

const fmt = (d: Dayjs): string => d.format('YYYY-MM-DD');

/**
 * 集計期間（dateFrom〜dateTo）を実際の日付範囲に解決する。
 * preset に応じて算出し、CUSTOM のときは from/to をそのまま使う。
 * 不正なカスタム範囲（from>to・空）は今月へフォールバックする（復元ガードと同方針）。
 * ALL（全期間）は from/to を undefined にして BE 側で全件集計させる。
 */
export const resolveDateRange = (
  range: ExpenseDateRange,
): { dateFrom?: string; dateTo?: string } => {
  const now = dayjs();
  switch (range.preset) {
    case 'THIS_MONTH':
      return { dateFrom: fmt(now.startOf('month')), dateTo: fmt(now.endOf('month')) };
    case 'LAST_MONTH': {
      const last = now.subtract(1, 'month');
      return { dateFrom: fmt(last.startOf('month')), dateTo: fmt(last.endOf('month')) };
    }
    case 'THIS_YEAR':
      return { dateFrom: fmt(now.startOf('year')), dateTo: fmt(now.endOf('year')) };
    case 'ALL':
      return {};
    case 'CUSTOM': {
      const from = range.from ? dayjs(range.from) : null;
      const to = range.to ? dayjs(range.to) : null;
      // 不正（欠損・開始>終了）は今月へフォールバック
      if (!from || !to || !from.isValid() || !to.isValid() || from.isAfter(to)) {
        return { dateFrom: fmt(now.startOf('month')), dateTo: fmt(now.endOf('month')) };
      }
      return { dateFrom: fmt(from), dateTo: fmt(to) };
    }
    default:
      return { dateFrom: fmt(now.startOf('month')), dateTo: fmt(now.endOf('month')) };
  }
};

type Props = {
  value: ExpenseDateRange;
  onChange: (range: ExpenseDateRange) => void;
};

/**
 * 集計期間セレクタ（Home / Dashboard 共通）。
 * プリセット（今月/先月/今年/全期間/カスタム）＋ カスタム日付範囲。
 * 値は user_profile('expense-dashboard') で2画面共有する（親が useDashboardProfile で管理）。
 */
const ExpensePeriodSelector: React.FC<Props> = ({ value, onChange }) => {
  const handlePreset = (_: React.MouseEvent<HTMLElement>, preset: ExpensePeriodPreset | null) => {
    if (!preset) return;
    onChange({ ...value, preset });
  };

  const toDayjs = (v: string): Dayjs | null => (v ? dayjs(v) : null);

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      flexWrap="wrap"
      useFlexGap
    >
      <ToggleButtonGroup
        exclusive
        size="small"
        value={value.preset}
        onChange={handlePreset}
        sx={{
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            px: 1.5,
          },
        }}
      >
        {PERIOD_PRESET_KEYS.map((key) => (
          <ToggleButton key={key} value={key}>
            {ExpensePeriodPreset[key]}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {value.preset === 'CUSTOM' && (
        <Stack direction="row" spacing={1} alignItems="center">
          <DatePicker
            label="開始"
            value={toDayjs(value.from)}
            onChange={(d) => onChange({ ...value, from: d && d.isValid() ? fmt(d) : '' })}
            format="YYYY/MM/DD"
            slotProps={{
              textField: {
                size: 'small',
                sx: { width: 150, '& .MuiOutlinedInput-root': { borderRadius: 2 } },
              },
            }}
          />
          <DatePicker
            label="終了"
            value={toDayjs(value.to)}
            onChange={(d) => onChange({ ...value, to: d && d.isValid() ? fmt(d) : '' })}
            format="YYYY/MM/DD"
            slotProps={{
              textField: {
                size: 'small',
                sx: { width: 150, '& .MuiOutlinedInput-root': { borderRadius: 2 } },
              },
            }}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default ExpensePeriodSelector;
